import type { QueriesResults } from "@tanstack/react-query";
import { useQueries } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import {
    type LeadsSearchResponseDto,
    isLeadsType,
    type LeadsType,
    selectIsLeadsSearchDtoReady,
    selectListsSearchResultsDtoByLeadsType,
} from "app/pages/sales-intelligence/modules/leads-search";
import { useIsLeadsCriticalDataLoading } from "app/pages/sales-intelligence/modules/leads-search/hooks/useIsLeadsCriticalDataLoading";
import { useSearchDemoMode } from "app/pages/sales-intelligence/modules/leads-search/hooks/useSearchDemoMode";
import { useListParams } from "app/pages/sales-intelligence/modules/lists/context/ListParamsContext";
import type { PageTabsConfig } from "app/pages/sales-intelligence/pages/opportunity-list/types/page-tabs";

import { createLeadsSearchResultsQuery } from "./options";

type RawLeadsQueries = QueriesResults<LeadsSearchResponseDto[]>;

const useLeadsSearchResultsQueries = (tabsConfig: PageTabsConfig): RawLeadsQueries => {
    const listParams = useListParams();

    const isLeadsSearchDtoReady = useSelector(selectIsLeadsSearchDtoReady);
    const searchResultsDtoByLeadsType = useSelector(selectListsSearchResultsDtoByLeadsType);

    const isLeadsCriticalDataLoading = useIsLeadsCriticalDataLoading();
    const demoMode = useSearchDemoMode();

    const leadsTabs = tabsConfig.filter(({ id }) => isLeadsType(id)) as PageTabsConfig<LeadsType>;

    return useQueries({
        queries: leadsTabs.map(({ id }) =>
            createLeadsSearchResultsQuery({
                leadsType: id,
                dto: searchResultsDtoByLeadsType[id],
                listParams,
                demoMode,
                enabled: isLeadsSearchDtoReady && !isLeadsCriticalDataLoading,
                // The reader twin (`useLeadsSearchResult`) observes this same key and is what surfaces the
                // error to the boundary; a background tab must not tear the page down on its own.
                throwOnError: false,
            }),
        ),
    });
};

export default useLeadsSearchResultsQueries;
